/* eslint-disable max-len */
/**
 * @fileoverview "In Progress" card for the Project Overview page.
 *               Shows resumable workflow drafts with format, step, and time info.
 *               PATH: src/components/projects/WorkflowDraftsCard.js
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import {
    FiLayout, FiLayers, FiVideo, FiCamera, FiFilm,
    FiPlay, FiTrash2, FiEdit3, FiLoader,
} from 'react-icons/fi';
import { useToast } from '../shared/contexts/ToastContext';
import { useConfirmDialog } from '../shared/contexts/ConfirmContext';
import { STEP_TITLES } from '../shared/constants/workflowCopy';

const getWorkflowDraftsFn = httpsCallable(functions, 'getWorkflowDrafts');
const deleteWorkflowDraftFn = httpsCallable(functions, 'deleteWorkflowDraft');

// Icon map for format display
const FORMAT_ICONS = {
    standard: <FiLayout className="h-4 w-4" />,
    carousel: <FiLayers className="h-4 w-4" />,
    reel: <FiVideo className="h-4 w-4" />,
    story_image: <FiCamera className="h-4 w-4" />,
    story_video: <FiFilm className="h-4 w-4" />,
};

const FORMAT_LABELS = {
    standard: 'Standard Post',
    carousel: 'Carousel',
    reel: 'Reel',
    story_image: 'Story (Image)',
    story_video: 'Story (Video)',
};

// Step key → friendly title
const STEP_LABEL_MAP = {
    mission_briefing: STEP_TITLES.MISSION_BRIEFING,
    spark: STEP_TITLES.SPARK,
    clarification: STEP_TITLES.CLARIFICATION,
    fine_tune: STEP_TITLES.FINE_TUNE,
    plan: STEP_TITLES.PLAN,
    preview: STEP_TITLES.PREVIEW,
    visuals: STEP_TITLES.VISUALS,
    publish: STEP_TITLES.PUBLISH,
};

function getRelativeTime(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
}

function WorkflowDraftsCard({ projectId }) {
    const navigate = useNavigate();
    const { addToast } = useToast();
    const { confirm } = useConfirmDialog();
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);

    // Fetch drafts on mount
    useEffect(() => {
        let cancelled = false;

        const fetchDrafts = async () => {
            try {
                const result = await getWorkflowDraftsFn({ projectId });
                if (!cancelled && result.data?.success) {
                    setDrafts(result.data.drafts || []);
                }
            } catch (err) {
                // Silently fail — drafts are non-critical
                console.warn('Failed to load workflow drafts:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchDrafts();
        return () => { cancelled = true; };
    }, [projectId]);

    const handleResume = useCallback((draftId) => {
        navigate(`/project/${projectId}/content-hub?draft=${draftId}`);
    }, [navigate, projectId]);

    const handleDiscard = useCallback(async (draftId) => {
        const confirmed = await confirm({
            title: 'Discard Draft?',
            message: 'This will permanently delete this in-progress workflow. This action cannot be undone.',
            confirmLabel: 'Discard',
            isDangerous: true,
        });

        if (!confirmed) return;

        setDeletingId(draftId);
        try {
            await deleteWorkflowDraftFn({ projectId, draftId });
            setDrafts(prev => prev.filter(d => d.id !== draftId));
            addToast('Draft discarded.', 'success');
        } catch (err) {
            addToast('Failed to discard draft. Please try again.', 'error');
        } finally {
            setDeletingId(null);
        }
    }, [projectId, confirm, addToast]);

    // Don't render anything if no drafts or still loading
    if (loading || drafts.length === 0) return null;

    return (
        <div className="mb-8">
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-white/5 flex items-center gap-2">
                    <FiEdit3 className="h-4 w-4 text-slate-blue" />
                    <h3 className="text-sm font-black text-cream uppercase tracking-widest">In Progress</h3>
                    <span className="ml-1 text-xs text-cream/40 font-medium">({drafts.length})</span>
                </div>

                {/* Draft list */}
                <div className="divide-y divide-white/5">
                    {drafts.map(draft => {
                        const format = draft.workflowConfig?.format || 'standard';
                        const platforms = draft.workflowConfig?.platforms || [];
                        const platformLabel = platforms.length === 2 ? 'Meta' : platforms[0] ? platforms[0].charAt(0).toUpperCase() + platforms[0].slice(1) : '';
                        const stepLabel = STEP_LABEL_MAP[draft.currentStep] || draft.currentStep;
                        const isDeleting = deletingId === draft.id;

                        return (
                            <div key={draft.id} className="px-6 py-4 flex items-center justify-between gap-4 group hover:bg-white/5 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="p-2 rounded-lg bg-slate-blue/10 text-slate-blue flex-shrink-0">
                                        {FORMAT_ICONS[format] || <FiLayout className="h-4 w-4" />}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-cream truncate">
                                            {FORMAT_LABELS[format] || 'Post'}{platformLabel ? ` \u00B7 ${platformLabel}` : ''}
                                            {draft.workflowConfig?.workflow === 'stream' && (
                                                <span className="ml-1 text-pastel-green font-medium">(Series)</span>
                                            )}
                                        </p>
                                        <p className="text-xs text-cream/40 mt-0.5">
                                            Step: <span className="text-cream/60 font-medium">{stepLabel}</span>
                                            {draft.updatedAt && (
                                                <span className="ml-2">{getRelativeTime(draft.updatedAt)}</span>
                                            )}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                        onClick={() => handleResume(draft.id)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-warm-dark bg-slate-blue hover:bg-slate-blue/80 rounded-lg transition-colors"
                                    >
                                        <FiPlay className="h-3 w-3" />
                                        Resume
                                    </button>
                                    <button
                                        onClick={() => handleDiscard(draft.id)}
                                        disabled={isDeleting}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-cream/40 hover:text-soft-coral hover:bg-soft-coral/10 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        {isDeleting ? <FiLoader className="h-3 w-3 animate-spin" /> : <FiTrash2 className="h-3 w-3" />}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

WorkflowDraftsCard.propTypes = {
    projectId: PropTypes.string.isRequired,
};

export default WorkflowDraftsCard;
